import CardTemplate from "@appsinti/ui-core/CardTemplate";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { CardContentContainer } from "../../../cardStyles";
import { usePreviewData } from "../../../data";
import { useCurrencySelector } from "@appsinti/currency-provider";
import Current from "./Current";
import Day from "./Day";
import { useDateProvider } from "@appsinti/date-provider";

const HeaderContainer = styled(CardTemplate)<{ $height: number | null; $shouldBeVisible: boolean; $gap?: number; }>`
  max-width: 100%;
  width: 100%;
  overflow: hidden;
  flex-grow: 0;
  flex-shrink: 0;
  transition: height 0.3s ease;
  flex: unset;
  margin-bottom: ${({ $gap, $shouldBeVisible }) =>
    $gap && $shouldBeVisible ? $gap : 0}px;

  ${({ $height }) => ($height ? `height: ${$height}px;` : "")};
  ${({ $shouldBeVisible }) => ($shouldBeVisible ? "" : "outline: none;")};

  .body {
    display: flex;
    flex-direction: column;
    flex: unset;
  }
`;

const HeaderTitleContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 3px 6px;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  flex-grow: 1;

  p {
    margin: 0;
    padding: 0;
    font-size: 1.4rem;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-grow: 1;
    flex-shrink: 1;
  }

  @media screen and (max-height: 750px) {
    padding: 2px 4px;
    p {
      font-size: 1.2rem;
    }
  }

  @media screen and (max-height: 710px) {
    padding: 1px 2px;
  }
`;

const CombinedLocationsHeader: React.FC<{
  headerHeight: number | null;
  setHeaderHeight: React.Dispatch<React.SetStateAction<number | null>>;
  gap: number;
}> = ({ headerHeight, setHeaderHeight, gap }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { preview, day, loadingAnyDay } = usePreviewData();
  const { currentDate } = useDateProvider();
  const { currencyEnabled, currency } = useCurrencySelector();

  const previewVisible: boolean = useMemo(() => {
    if (!preview) {
      return false;
    }

    if (currencyEnabled && currency) {
      const validGroups: boolean[] = [];
      for (const group of Object.values(preview)) {
        if (!!group.salesData.conversions?.[currency]) {
          validGroups.push(true);
        }
      }
      return validGroups.length > 0;
    }

    const currencies = Object.values(preview).map((group) => {
      return group.salesData.currency;
    });

    const uniqueCurrencies = new Set(currencies);
    return uniqueCurrencies.size === 1;
  }, [preview, currencyEnabled, currency]);

  const dayVisible: boolean = useMemo(() => {
    if (previewVisible && loadingAnyDay) {
      return true;
    }

    if (!day || !currentDate) {
      return false;
    }

    if (currencyEnabled && currency) {
      const validGroups: boolean[] = [];
      for (const group of Object.values(day)) {
        if (!!group.salesData.conversions?.[currency]) {
          validGroups.push(true);
        }
      }
      return validGroups.length > 0;
    }

    const currencies = Object.values(day).map((group) => {
      return group.salesData.currency;
    });

    const uniqueCurrencies = new Set(currencies);
    return uniqueCurrencies.size === 1;
  }, [day, loadingAnyDay, currencyEnabled, currency, previewVisible, currentDate]);

  const isVisible = previewVisible || dayVisible;

  useEffect(() => {
    if (containerRef.current) {
      if (isVisible) {
        setHeaderHeight(containerRef.current.scrollHeight);
        return;
      }
      setHeaderHeight(0);
      return;
    }
    setHeaderHeight(null);
  }, [isVisible, containerRef.current?.scrollHeight]);

  return preview ? (
    <HeaderContainer
      background={"white"}
      noPadding={true}
      $height={headerHeight}
      $shouldBeVisible={isVisible}
      $gap={gap}
      bodyRef={containerRef}
      body={
        <>
          <HeaderTitleContainer>
            <p>All Locations</p>
          </HeaderTitleContainer>
          <CardContentContainer>
            {!currentDate && <Current stats={preview} />}
            {currentDate && <Day stats={day} loading={loadingAnyDay} />}
          </CardContentContainer>
        </>
      }
    />
  ) : null;
};

export default CombinedLocationsHeader;
